import { useState } from 'react';
import { AppRoute } from '@/App';
import { useNavigate } from 'react-router-dom';
import { authService } from '@/services/authService';

export interface RegisterScreenViewModel {
  // Состояние формы
  email: string;
  password: string;
  confirmPassword: string;
  passwordVisible: boolean;
  confirmPasswordVisible: boolean;
  name: string;
  error: string | null;
  birthDate: string;
  nameError: string | null;
  birthDateError: string | null;
  step: RegisterStep;
  setStep: (step: RegisterStep) => void;
  
  // Состояние ошибок
  emailError: string | null;
  passwordError: string | null;
  confirmPasswordError: string | null;
  setName: (name: string) => void;
  setBirthDate: (birthDate: string) => void;

  // Методы управления формой
  setEmail: (email: string) => void;
  setPassword: (password: string) => void;
  setConfirmPassword: (password: string) => void;
  togglePasswordVisibility: () => void;
  toggleConfirmPasswordVisibility: () => void;
  
  // Методы навигации
  handleSubmitStep1: () => void;
  handleSubmitStep2: () => void;
  navigateToLogin: () => void;
}

export enum RegisterStep {
  Step1 = 1,
  Step2 = 2,
}

export const useRegisterScreenViewModel = (): RegisterScreenViewModel => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [birthDateError, setBirthDateError] = useState<string | null>(null);
  const [step, setStep] = useState(RegisterStep.Step1);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const validateEmail = (email: string): string | null => {
    if (!email.trim()) {
      return "Введите email";
    }
    if (!email.includes("@")) {
      return "Введите правильный email";
    }
    return null;
  };
  const validateName = (name: string): string | null => {
    if (!name.trim()) {
      return "Введите имя";
    }
    return null;
  };
  const validateBirthDate = (birthDate: string): string | null => {
    if (!birthDate.trim()) {
      return "Введите дату рождения";
    }
    return null;
  };
  

  const validatePassword = (password: string): string | null => {
    if (!password.trim()) {
      return "Введите пароль";
    }
    if (password.length < 8) {
      return "Пароль должен содержать не менее 8 символов";
    }
    return null;
  };

  const validateConfirmPassword = (password: string, confirmPassword: string): string | null => {
    if (!confirmPassword.trim()) {
      return "Подтвердите пароль";
    }
    if (password !== confirmPassword) {
      return "Пароли не совпадают";
    }
    return null;
  };


const validateFormStep1 = () => {
  const nameValidation = validateName(name);
  const birthDateValidation = validateBirthDate(birthDate);
  setNameError(nameValidation);
  setBirthDateError(birthDateValidation);
  return !nameValidation && !birthDateValidation;
}
const validateFormStep2 = () => {
  const emailValidation = validateEmail(email);
  const passwordValidation = validatePassword(password);
  const confirmPasswordValidation = validateConfirmPassword(password, confirmPassword);
  setEmailError(emailValidation);
  setPasswordError(passwordValidation);
  setConfirmPasswordError(confirmPasswordValidation);
  return !emailValidation && !passwordValidation && !confirmPasswordValidation;
}


  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const toggleConfirmPasswordVisibility = () => {
    setConfirmPasswordVisible(!confirmPasswordVisible);
  };

  const handleSubmitStep1 = () => {
    if (validateFormStep1()) {
      setStep(RegisterStep.Step2);
    }
  };

  const handleSubmitStep2 = async () => {
    if (validateFormStep2()) {
      try {
        const date = new Date(birthDate);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        
        const formattedDate = `${month}.${day}.${year}`;
        await authService.signUp(email, password, name, formattedDate);
      } catch (error: any) {
        if (error.response.status === 409) {
          setError("Пользователь с таким email уже существует");
        } else {
          setError("Произошла ошибка");
        }
      }
    }
  };

  const navigateToLogin = () => {
    navigate(AppRoute.Login);
  };

  return {
    name,
    birthDate,
    nameError,
    birthDateError,
    step,
    setStep,
    error,
    email,
    password,
    confirmPassword,
    passwordVisible,
    confirmPasswordVisible,
    emailError,
    passwordError,
    confirmPasswordError,
    setName,
    setBirthDate,
    setEmail,
    setPassword,
    setConfirmPassword,
    togglePasswordVisibility,
    toggleConfirmPasswordVisibility,
    handleSubmitStep1,
    handleSubmitStep2,
    navigateToLogin,
  };
}; 
